import { FC } from 'react'
import { Tooltip } from '@mui/material'
import theme from 'theme'
import { DEVICES_COLUMN_NAME, IDENTITY_COLUMN_NAME, IntegrationInstance } from 'models'

interface SetFilterCellRendererProps {
  value: any
  valueFormatted?: string
  api?: any
  colDef?: any
  column?: any
  context?: any
  // Custom params passed via cellRendererParams
  columnName?: string
  dataSourceOptions?: IntegrationInstance[]
}

const tooltipStyle = {
  backgroundColor: `${theme.colors.tooltipBg} !important`,
  color: theme.colors.menuItemText,

  '&::before': {
    backgroundColor: `${theme.colors.tooltipBg} !important`
  },

  '& .MuiTooltip-arrow::before': {
    backgroundColor: `${theme.colors.tooltipBg} !important`
  }
}

const tooltipComponentProps = {
  tooltip: {
    sx: tooltipStyle
  }
}

const displayValue = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  return String(value)
}

/**
 * Highlights matching text in a string based on search term
 * Returns JSX with highlighted portions wrapped in <mark> tags
 */
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || !text) {
    return text
  }

  const lowerText = text.toLowerCase()
  const lowerSearch = searchTerm.toLowerCase()
  const index = lowerText.indexOf(lowerSearch)

  if (index === -1) {
    return text
  }

  const before = text.slice(0, index)
  const match = text.slice(index, index + searchTerm.length)
  const after = text.slice(index + searchTerm.length)

  return (
    <>
      {before}
      <span style={{ color: '#ffa9fc' }}>{match}</span>
      {after}
    </>
  )
}

/**
 * Gets the current mini filter search text from the DOM
 * This is read synchronously during render since AG Grid re-renders
 * the filter list when the mini filter value changes
 */
const getMiniFilterText = (): string => {
  const miniFilterInput = document.querySelector('.ag-mini-filter input') as HTMLInputElement
  return miniFilterInput?.value || ''
}

const SetFilterCellRenderer: FC<SetFilterCellRendererProps> = (props) => {
  const { value, columnName, dataSourceOptions } = props

  // Read the current mini filter value directly from DOM
  // AG Grid re-renders the filter list when mini filter changes, so this is always current
  const searchText = getMiniFilterText()

  // Handle ONYXIA_PRODUCT_INSTANCE column - show friendly name but tooltip shows raw value
  const isInstanceColumn =
    columnName === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
    columnName === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE

  let displayText: string
  let tooltipText: string

  if (isInstanceColumn && dataSourceOptions) {
    const dataSource = dataSourceOptions.find((opt) => opt.instance_name === value)
    displayText = dataSource?.name || value
    tooltipText = displayValue(value) // Show raw value in tooltip (original behavior)
  } else {
    displayText = displayValue(value)
    tooltipText = displayValue(value)
  }

  const highlightedContent = highlightText(displayText, searchText)

  return (
    <Tooltip title={tooltipText} placement="right" arrow componentsProps={tooltipComponentProps}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%'
        }}
      >
        {highlightedContent}
      </div>
    </Tooltip>
  )
}

export default SetFilterCellRenderer
